'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { ArrowLeft, Bell, BellOff, Mic2, Video, Settings as SettingsIcon, ChevronRight, LayoutGrid, Rows3, Search, X, UserPlus, ClipboardPaste } from 'lucide-react'
import { useSubscriptions, type Subscription } from '@/lib/subscriptions'
import { Page, Chip, Input, EmptyState, Button, cx } from '@/components/ui/kit'
import SubscriptionsImport from './SubscriptionsImport'
import { SubAvatar, SubAvatarImg, SubActionSheet } from './abonnements/SubAvatars'
import SubsFeed from './abonnements/SubsFeed'
import AddSubscriptions, { type AddTab } from './abonnements/AddSubscriptions'

/**
 * Page « Mes abonnements » PARTAGÉE musique/vidéo (même source : token reco).
 * Présentation type plateforme :
 *   1. en-tête héro + compteur d'abonnements
 *   2. rangée d'avatars ronds (clic = filtre sur cet abonnement ; ⋯ / appui long
 *      = voir la chaîne, reclasser artiste ↔ chaîne, se désabonner)
 *   3. puces Tout / Artistes / Chaînes
 *   4. fil des dernières sorties groupées par date (/api/reco/video)
 *   5. encart d'import des abonnements YouTube (Takeout + extension + synchro)
 *   + boutons « Ajouter » (recherche par nom) et « Coller mes abonnements YouTube »
 *     (copier-coller de youtube.com/feed/channels) → fenêtre AddSubscriptions.
 * Contexte : 'music' → ARTISTES en premier, retour /app ; 'video' → CHAÎNES en
 * premier, retour /app/video.
 */
type Kind = 'all' | 'artist' | 'channel'

export default function SubscriptionsView({ context = 'video' }: { context?: 'music' | 'video' }) {
  const subs = useSubscriptions()
  const isVideo = context === 'video'
  const backHref = isVideo ? '/app/video' : '/app'
  const backLabel = isVideo ? 'Retour aux vidéos' : "Retour à l'accueil"
  const artists = subs.filter(s => s.type === 'artist')
  const channels = subs.filter(s => s.type !== 'artist')

  const [kind, setKind] = useState<Kind>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [q, setQ] = useState('')
  const [addTab, setAddTab] = useState<AddTab | null>(null)

  // Ordre : le type « du contexte » d'abord (artistes en musique, chaînes en vidéo).
  const ordered = useMemo(() => {
    const first = isVideo ? channels : artists
    const second = isVideo ? artists : channels
    return [...first, ...second]
  }, [isVideo, artists, channels])

  const shown = useMemo(() => {
    const byKind = kind === 'all' ? ordered : ordered.filter(s => (s.type === 'artist' ? 'artist' : 'channel') === kind)
    const s = q.trim().toLowerCase()
    return s ? byKind.filter(x => (x.title || '').toLowerCase().includes(s)) : byKind
  }, [ordered, kind, q])
  const byKind = useMemo(() => kind === 'all' ? subs : subs.filter(s => (s.type === 'artist' ? 'artist' : 'channel') === kind), [subs, kind])

  const selected: Subscription | null = subs.find(s => s.channelId === selectedId) || null
  const menuSub = subs.find(s => s.channelId === menuFor) || null

  // Sélection invalide (désabonné / reclassé hors du filtre) → on la retire.
  useEffect(() => {
    if (!selectedId) return
    const s = subs.find(x => x.channelId === selectedId)
    if (!s || (kind !== 'all' && (s.type === 'artist' ? 'artist' : 'channel') !== kind)) setSelectedId(null)
  }, [subs, selectedId, kind])

  const chips: { k: Kind; label: string; n: number; icon?: typeof Mic2 }[] = [
    { k: 'all', label: 'Tout', n: subs.length },
    ...(isVideo
      ? [{ k: 'channel' as Kind, label: 'Chaînes', n: channels.length, icon: Video }, { k: 'artist' as Kind, label: 'Artistes', n: artists.length, icon: Mic2 }]
      : [{ k: 'artist' as Kind, label: 'Artistes', n: artists.length, icon: Mic2 }, { k: 'channel' as Kind, label: 'Chaînes', n: channels.length, icon: Video }]),
  ]

  return (
    <Page className="pb-24">
      <Link href={backHref} className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-white/60 hover:text-white transition">
        <ArrowLeft size={16} /> {backLabel}
      </Link>

      {/* ===== Héro ===== */}
      <header data-reveal className="relative isolate overflow-hidden rounded-3xl border border-white/[0.07] bg-gradient-to-br from-adeep/80 via-spanel to-sbg p-5 sm:p-7 md:p-9">
        <div className="fx-aurora" aria-hidden><i /><i /><i /></div>
        <span aria-hidden className="pointer-events-none absolute -top-20 right-[-60px] w-80 h-80 rounded-full bg-a2/25 blur-3xl -z-10" />
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-white/55 mb-2">{isVideo ? 'BLC VIDEO' : 'BLC MÉDIAS'}</div>
            <h1 className="flex items-center gap-3 text-[32px] md:text-[46px] font-extrabold tracking-tight leading-[1.02]">
              <Bell size={34} className="text-a3 fill-a3/90 shrink-0" /> Abonnements
            </h1>
            <p className="text-[15px] text-white/60 mt-2 max-w-lg">
              {subs.length > 0
                ? (isVideo ? 'Les dernières vidéos des chaînes et artistes que tu suis, au même endroit.' : 'Les derniers titres des artistes et chaînes que tu suis, au même endroit.')
                : 'Suis tes artistes et chaînes préférés'}
            </p>
            {/* Ajout rapide : par nom, ou collage de la liste YouTube. */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button icon={UserPlus} onClick={() => setAddTab('name')}>Ajouter</Button>
              <Button variant="secondary" icon={ClipboardPaste} onClick={() => setAddTab('paste')}>Coller mes abonnements YouTube</Button>
            </div>
          </div>
          {subs.length > 0 && (
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Pile d'avatars */}
              <div className="hidden sm:flex -space-x-4">
                {ordered.slice(0, 4).map(s => (
                  <span key={s.channelId} className="rounded-full ring-[3px] ring-spanel"><SubAvatarImg sub={s} size={48} /></span>
                ))}
              </div>
              <div className="flex items-stretch gap-3">
                <Stat n={subs.length} label={`abonnement${subs.length > 1 ? 's' : ''}`} big />
                <Stat n={artists.length} label={`artiste${artists.length > 1 ? 's' : ''}`} />
                <Stat n={channels.length} label={`chaîne${channels.length > 1 ? 's' : ''}`} />
              </div>
            </div>
          )}
        </div>
      </header>

      {subs.length === 0 ? (
        <>
          <div className="mt-6">
            <EmptyState
              icon={BellOff}
              title="Aucun abonnement pour l'instant"
              text={<>
                {isVideo
                  ? <>Ouvre une chaîne (clique sur le nom d'une chaîne sous une vidéo) et clique sur <b>S'abonner</b>.</>
                  : <>Abonne-toi à un artiste (bouton <b>S'abonner</b> sur sa fiche) pour suivre ses nouveautés.</>}
                <br />Ou clique sur <b>Ajouter</b> / <b>Coller mes abonnements YouTube</b> ci-dessus, ou importe-les ci-dessous.
              </>}
            />
          </div>
          <SubscriptionsImport className="mt-6" />
        </>
      ) : (
        <>
          {/* ===== Puces de filtre + bascule rangée/grille ===== */}
          <div className="mt-6 md:mt-8 flex items-center gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
            {chips.map(c => (
              <Chip key={c.k} active={kind === c.k} onClick={() => setKind(c.k)} className="!px-4 !py-2 !text-[14px]">
                <span className="inline-flex items-center gap-1.5">
                  {c.icon && <c.icon size={14} />}{c.label}
                  <span className={cx('tabular-nums', kind === c.k ? 'text-white/85' : 'text-white/45')}>{c.n}</span>
                </span>
              </Chip>
            ))}
            <span className="flex-1" />
            <button type="button" onClick={() => { setExpanded(v => !v); if (expanded) setQ('') }}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-semibold text-a4 hover:text-white bg-white/[0.04] border border-white/10 hover:bg-white/[0.1] transition">
              {expanded ? <><Rows3 size={15} /> Rangée</> : <><LayoutGrid size={15} /> Tout afficher</>}
            </button>
          </div>

          {/* ===== Avatars des abonnements ===== */}
          <section data-reveal className="mt-4">
            {expanded && (
              <Input icon={Search} value={q} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
                placeholder="Rechercher un abonnement…" className="mb-4 max-w-md" />
            )}
            {shown.length === 0 ? (
              <p className="text-[14px] text-white/50 py-4">{q ? <>Aucun abonnement ne correspond à « {q} ».</> : 'Aucun abonnement de ce type.'}</p>
            ) : (
              <div data-stagger data-reveal className={expanded
                ? 'grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-x-2 gap-y-5 justify-items-center'
                : 'flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide pt-1 pb-2 -mx-4 px-4 sm:mx-0 sm:px-0'}>
                {shown.map(s => (
                  <SubAvatar key={s.channelId} sub={s} compact={expanded}
                    selected={selectedId === s.channelId}
                    dimmed={!!selectedId && selectedId !== s.channelId}
                    onSelect={() => setSelectedId(id => id === s.channelId ? null : s.channelId)}
                    onMenu={() => setMenuFor(s.channelId)} />
                ))}
              </div>
            )}
            {selected && (
              <div className="mt-3 flex items-center gap-2 flex-wrap text-[14px] text-white/60">
                Filtré sur <b className="text-white">{selected.title}</b>
                <button type="button" onClick={() => setSelectedId(null)}
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12.5px] font-semibold text-a4 bg-a2/15 hover:bg-a2/25 transition">
                  <X size={13} /> Retirer le filtre
                </button>
              </div>
            )}
          </section>

          {/* ===== Fil des nouveautés ===== */}
          <SubsFeed context={context} kind={kind} selected={selected} subs={byKind} />

          {/* ===== Import ===== */}
          <SubscriptionsImport className="mt-12" />
        </>
      )}

      {/* Petit renvoi : la connexion Jarvis est dans les Paramètres. */}
      <Link href="/app/settings" className="mt-5 inline-flex items-center gap-2 text-[13px] text-white/50 hover:text-a4 transition">
        <SettingsIcon size={14} /> Connexion Jarvis → dans les Paramètres <ChevronRight size={14} />
      </Link>

      {menuSub && <SubActionSheet sub={menuSub} onClose={() => setMenuFor(null)} />}
      {addTab && <AddSubscriptions initialTab={addTab} onClose={() => setAddTab(null)} />}
    </Page>
  )
}

function Stat({ n, label, big = false }: { n: number; label: string; big?: boolean }) {
  return (
    <div className={cx('rounded-2xl px-3.5 sm:px-4 py-2.5 border text-center min-w-[72px]',
      big ? 'bg-gradient-to-br from-a1 to-a2 border-transparent shadow-[0_0_24px_rgb(var(--a2)/.45)]' : 'bg-white/[0.05] border-white/10')}>
      <div className={cx('font-extrabold tabular-nums leading-none', big ? 'on-media text-white text-[28px] sm:text-[34px]' : 'text-[22px] sm:text-[26px]')}>{n}</div>
      <div className={cx('mt-1 text-[11.5px] font-semibold', big ? 'on-media text-white/85' : 'text-white/55')}>{label}</div>
    </div>
  )
}
